<template>
  <div class="stat-card" :class="{ 'stat-card--compact': compact }">
    <div class="stat-card__header">
      <span class="stat-card__label">{{ label }}</span>
      <div
        v-if="icon"
        class="stat-card__icon"
        :style="{ background: iconBackground }"
      >
        <component :is="iconComponent" v-if="iconComponent" />
        <span v-else class="stat-card__icon-text">{{ icon }}</span>
      </div>
    </div>
    <div class="stat-card__value-row">
      <span class="stat-card__value">{{ formattedValue }}</span>
      <span v-if="suffix" class="stat-card__suffix">{{ suffix }}</span>
    </div>
    <div v-if="showTrend" class="stat-card__trend" :class="trendClass">
      <svg
        v-if="trend > 0"
        class="stat-card__trend-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" />
        <polyline points="17,6 23,6 23,12" />
      </svg>
      <svg
        v-else-if="trend < 0"
        class="stat-card__trend-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="23,18 13.5,8.5 8.5,13.5 1,6" />
        <polyline points="17,18 23,18 23,12" />
      </svg>
      <span class="stat-card__trend-value">{{ formattedTrend }}</span>
      <span v-if="trendLabel" class="stat-card__trend-label">{{
        trendLabel
      }}</span>
    </div>
    <div v-if="$slots.footer" class="stat-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
/**
 * StatCard - KPI display card with trend indicator
 * @description Shows a metric value with optional trend indicator and icon
 * @prop {String} label - Metric label/title
 * @prop {Number|String} value - The metric value
 * @prop {String} suffix - Value suffix (e.g., "EUR", "%", "Mo")
 * @prop {Number} trend - Trend percentage (positive/negative)
 * @prop {String} trendLabel - Label for the trend (e.g., "vs. letzter Monat")
 * @prop {String} icon - Icon character or name
 * @prop {String} iconBackground - Background color for icon
 * @prop {Boolean} compact - Use compact layout
 * @prop {String} format - Value format type (number, currency, percent)
 */
export default {
  name: "StatCard",
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [Number, String],
      required: true,
    },
    suffix: {
      type: String,
      default: "",
    },
    trend: {
      type: Number,
      default: null,
    },
    trendLabel: {
      type: String,
      default: "vs. Vormonat",
    },
    icon: {
      type: String,
      default: "",
    },
    iconBackground: {
      type: String,
      default: "var(--k-accent)",
    },
    iconComponent: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: "number",
      validator: (v) => ["number", "currency", "percent", "none"].includes(v),
    },
  },
  computed: {
    /**
     * Formats the value based on format prop
     * @returns {String} Formatted value string
     */
    formattedValue() {
      if (this.format === "none" || typeof this.value === "string") {
        return this.value;
      }

      const num = Number(this.value);
      if (isNaN(num)) return this.value;

      if (this.format === "currency") {
        return this.formatCurrency(num);
      }

      if (this.format === "percent") {
        return num.toFixed(1) + "%";
      }

      return this.formatNumber(num);
    },
    /**
     * Determines if trend should be shown
     * @returns {Boolean}
     */
    showTrend() {
      return this.trend !== null && this.trend !== undefined;
    },
    /**
     * Formats the trend value with sign
     * @returns {String}
     */
    formattedTrend() {
      if (!this.showTrend) return "";
      const sign = this.trend > 0 ? "+" : "";
      return `${sign}${this.trend.toFixed(1)}%`;
    },
    /**
     * Returns CSS class based on trend direction
     * @returns {String}
     */
    trendClass() {
      if (this.trend > 0) return "stat-card__trend--positive";
      if (this.trend < 0) return "stat-card__trend--negative";
      return "stat-card__trend--neutral";
    },
  },
  methods: {
    /**
     * Formats a number with locale-specific separators
     * @param {Number} num - Number to format
     * @returns {String}
     */
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + "M";
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num.toLocaleString("de-DE");
    },
    /**
     * Formats a number as currency
     * @param {Number} num - Number to format
     * @returns {String}
     */
    formatCurrency(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + "M";
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num.toLocaleString("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>

<style scoped>
.stat-card {
  background: var(--k-bg-glass);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--k-bg-glass-border);
  border-radius: var(--k-radius-lg);
  box-shadow: var(--k-shadow-md);
  padding: 24px;
  transition: transform var(--k-transition-normal),
    box-shadow var(--k-transition-normal);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--k-shadow-lg);
}

.stat-card--compact {
  padding: 16px;
}

.stat-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.stat-card__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--k-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-card__icon {
  width: 36px;
  height: 36px;
  border-radius: var(--k-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.stat-card__icon svg {
  width: 20px;
  height: 20px;
}

.stat-card__value-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}

.stat-card__value {
  font-size: 32px;
  font-weight: 600;
  color: var(--k-text-primary);
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.stat-card--compact .stat-card__value {
  font-size: 24px;
}

.stat-card__suffix {
  font-size: 16px;
  font-weight: 500;
  color: var(--k-text-secondary);
}

.stat-card__trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
}

.stat-card__trend--positive {
  color: var(--k-success);
}

.stat-card__trend--negative {
  color: var(--k-danger);
}

.stat-card__trend--neutral {
  color: var(--k-text-tertiary);
}

.stat-card__trend-icon {
  width: 16px;
  height: 16px;
}

.stat-card__trend-value {
  font-weight: 600;
}

.stat-card__trend-label {
  color: var(--k-text-tertiary);
  font-weight: 400;
  margin-left: 4px;
}

.stat-card__footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--k-border-color);
}
</style>

